import cookie from '../cookie';
import env from '../env';
import logger from '../logger';
import { request } from '../request';
import { globalObject } from '../system';
import { buildUrl } from '../url';
import util, { getUrlParam } from '../util';
import * as config from './config';
import { clearPageCookies, clearRootDomainCookies, isNativeLogin } from './helper';
import IframeCookieLoader from './IframeCookieLoader';
import * as reporter from './report';

const VAPPID = 11059694;
const VSECRET = 'fdf61a6be0aad57132bc5cdf78ac30145b6cd2c1470b0cfe';
const COOKIE_EXPIRED_TIME = 2592000; // 30天有效期
const CGI = '//access.video.qq.com/user/';
const VIDEO_COOKIE_KEYS = [
  'main_login',
  'vuserid',
  'vusession',
  'access_token',
  'refresh_token',
  'openid',
  'appid',
  'vqq_vuserid',
  'vqq_vusession',
  'vqq_access_token',
  'vqq_openid',
  'vqq_appid',
  'video_guid',
  'video_platform',
];

const isDebug = globalObject.location ? globalObject.location.search.indexOf('test=1') > -1 : false;

/**
 * 判断当前是否是 video.qq.com 域
 * @returns {Boolean}
 */
function isVideoDomain() {
  return /\.video\.qq\.com/.test(location.host);
}

// 接口请求次数
export function vRequest(curl, data = {}, options) {
  let url = curl;
  if (!url.match(/^(https?:)?\/\//)) {
    url = CGI + url;
    data = {
      ...data,
      raw: 1,
      vappid: VAPPID,
      vsecret: VSECRET,
    };
  }
  return new Promise((resolve, reject) => {
    request(url, data, {
      dataType: 'jsonp',
      ...options,
    }).then((res) => {
      let errmsg = '';
      let errcode = 0;
      if (res && 'errcode' in res && res.errcode !== 0) {
        errcode = res.errcode;
        errmsg = res.errmsg || res.msg;
      } else if ('result' in res && res.result && res.result.code !== 0) {
        errcode = res.result.code;
        errmsg = res.result.msg;
      }
      resolve(res);
      if (!curl.match(/^(https?:)?\/\//)) {
        reporter.report({
          f_event: reporter.EVENT_INIT_REQUEST,
          f_cgi: curl,
          f_cgi_param: JSON.stringify(data),
          f_errcode: errcode,
          f_errmsg: errmsg,
        });
      }
    })
      .catch((e) => {
        reject(e);
      });
  });
}

/**
 * 获取登录态类型
 */
export function loginType() {
  const loginType = cookie.get('main_login');
  if (!loginType && getUin() && getSkey()) {
    return 'qq';
  }
  return loginType || '';
}

/**
 * 获取当前需要同步 cookie 的域名
 * @returns {String}
 */
export function getDomain() {
  const domain = location.host;

  // 支持域名：(*.)?film.qq.com, (*.)?v.qq.com, *.video.qq.com
  if (!/(film|v)(\.video)?\.qq\.com/.test(domain)) {
    return '';
  }

  const arr = domain.split('.');
  const len = arr.length;

  if (len <= 3 || /\.video\.qq\.com/.test(domain)) {
    return domain;
  }

  return arr.slice(len - 3, len).join('.');
}

/**
 * 同步登录态
 */
export function syncCookies(callback) {
  // 通过平台号确定是否需要同步cookie
  if (env.isApp || isNativeLogin() || isVideoDomain()) {
    callback && callback(null);
    return;
  }

  _loadVideoCookie((vcookie) => {
    if (vcookie) {
      const cookies = vcookie.getCookie();

      // 通过平台号确定是否需要同步cookie
      const cookieDomain = getDomain();

      const preType = cookies.main_login === 'qq' ? 'wx' : 'qq';
      if (cookie.get(`${preType}_nick`) || cookie.get(`${preType}_head`)) {
        cookie.remove(`${preType}_nick`, {
          domain: cookieDomain,
          path: '/',
        });
        cookie.remove(`${preType}_head`, {
          domain: cookieDomain,
          path: '/',
        });
      }

      // 同步 cookie
      VIDEO_COOKIE_KEYS.forEach((key) => {
        // fix: 以前的逻辑 cookie 是种在当前域，需要先移出当前域名的 cookie 信息，再同步
        // 新逻辑 cookie 种在三级域名，如 m.v.qq.com，cookie 种在 v.qq.com
        if (cookieDomain !== location.host) {
          cookie.remove(key, {
            domain: location.host,
          });
        }
        cookie.set(key, cookies[key] || '', {
          expires: cookies[key] ? COOKIE_EXPIRED_TIME : -24,
          domain: cookieDomain,
          path: '/',
        });
      });
      callback && callback(cookies);
    } else {
      callback && callback(null);
    }
  });
  reporter.report({
    f_event: reporter.EVENT_INIT_SYNC,
  });
}

export async function _loadVideoCookie(callback) {
  const cookies = await IframeCookieLoader.getInstance().fetch();
  if (typeof callback === 'function') {
    try {
      callback(cookies);
    } catch (e) {
      logger.error(`[login/lib/_loadVideoCookie]error in callback.(${e})`);
    }
  }

  return cookies;
}

/**
 * 续期登录态
 */
let _refreshTime = 0;
const _maxRefreshTime = 2;
export function refreshAuthToken(type, callback) {
  if (typeof type === 'function') {
    callback = type;
    type = loginType() || 'qq';
  }

  // TV登录态不过续期
  const appid = cookie.get('vqq_appid') || cookie.get('appid');
  if (config.TV_APPIDS.indexOf(appid) > -1) {
    return callback();
  }
  const csrfParam = buildCSRF();
  vRequest('auth_refresh', { type, ...csrfParam }, {
    timeout: 1500,
  }).then((data) => {
    if (data && data.errcode === 0) {
      const cookieDomain = getDomain();
      const cookieOption = {
        expires: COOKIE_EXPIRED_TIME,
        domain: cookieDomain,
        path: '/',
      };
      // 无需再次同步登录态信息到 video.qq.com 域包括子域，避免冲突
      if (!isVideoDomain()) {
        if (type === 'qq' && cookie.get('vqq_appid') && cookie.get('vqq_openid')) {
          const PREFIX = type === 'qq' ? 'vqq_' : '';
          Object.keys(data).forEach((key) => {
            if (VIDEO_COOKIE_KEYS.indexOf(key) > -1) {
              cookie.set(PREFIX + key, data[key] || '', {
                expires: data[key] ? COOKIE_EXPIRED_TIME : -24,
                domain: cookieDomain,
                path: '/',
              });
            }
          });
        } else {
          cookie.set('vuserid', data.vuserid, cookieOption);
          cookie.set('vusession', data.vusession, cookieOption);
        }
      }
      // 同步用户头像昵称，用于展示用
      cookie.set(`${type}_nick`, data.nick, cookieOption);
      cookie.set(`${type}_head`, data.head, cookieOption);
      cookie.set('last_refresh_time', +new Date(), cookieOption);
      cookie.set('last_refresh_vuserid', data.vuserid, cookieOption);
      updateAuthRefreshTimer(data.next_refresh_time);
      callback && callback();
    } else {
      if (data && [-1001, -1002, -1003, -1004, 1006, 1111, 1112].indexOf(data.errcode) > -1) {
        clearCookie(callback);
      } else {
        if (_refreshTime < _maxRefreshTime) {
          refreshAuthToken(callback);
        } else {
          callback && callback();
        }
        _refreshTime += 1;
      }
    }
  })
    .catch(() => {
      if (_refreshTime < _maxRefreshTime) {
        refreshAuthToken(callback);
      } else {
        callback && callback();
      }
      _refreshTime += 1;
    });
}

/**
 * 续期定时器
 * @param {Number} nextTime 下次续期时间/s
 */
let _refreshTimer = null;
function updateAuthRefreshTimer(nextTime = 0) {
  if (_refreshTimer) clearTimeout(_refreshTimer);

  if (nextTime > 0) {
    _refreshTimer = setTimeout(() => {
      refreshAuthToken();
      clearTimeout(_refreshTimer);
    }, nextTime * 1000);
  }
}

/**
 * PTLogin 登录态换视频票据
 */
let _authLoginRetryTime = 0;
export function skey2Vusession(callback) {
  const csrfParam = buildCSRF();
  vRequest('auth_login', {
    type: 'qq',
    login_flag: 1,
    ...csrfParam,
  }, {
    timeout: 3000,
  }).then((res) => {
    if (res && res.errcode === 0) {
      const options = {
        expires: COOKIE_EXPIRED_TIME,
        domain: getDomain(),
        path: '/',
      };
      if (!isVideoDomain()) {
        cookie.set('vuserid', res.vuserid, options);
        cookie.set('vusession', res.vusession, options);
        cookie.set('main_login', 'qq', options);
      }
      cookie.set('qq_nick', res.nick, options);
      cookie.set('qq_head', res.head, options);
      cookie.set('last_refresh_time', +new Date(), options);
      cookie.set('last_refresh_vuserid', res.vuserid, options);
      callback();
    } else if (res && [1006, 1111, 1112].indexOf(res.errcode) > -1) {
      // 票据过期，或者无效票据需要清掉 cookie 信息
      clearCookie(callback);
    } else {
      if (_authLoginRetryTime < _maxRefreshTime) {
        skey2Vusession(callback);
      } else {
        callback && callback();
      }
      _authLoginRetryTime += 1;
    }
  })
    .catch(() => {
      if (_authLoginRetryTime < _maxRefreshTime) {
        skey2Vusession(callback);
      } else {
        callback && callback();
      }
      _authLoginRetryTime += 1;
    });
  reporter.report({
    f_event: reporter.EVENT_INIT_LOGIN,
  });
}

let retryTime = 0;
export function innerToken2tenvideo(callback) {
  const msdkEncodeParam = getUrlParam('msdkEncodeParam');
  if (env.isMsdk && !msdkEncodeParam) {
    // 游戏场景，如果没有msdk参数，无法做票据转换，一般这种情况是已登录的case
    return callback();
  }
  // 先清Cookie，是为了避免拿到污染的票据信息
  // 由于换票据受限于客户端切换登录，不会同步清掉当前域下cookie
  // 在体育场景，登录类型字段 main_login 与主站一样，会造成切换账号后仍然拿到上一次票据信息
  clearCookie(() => {
    let loginType = '';
    const isMsdk = msdkEncodeParam.length > 0;
    let data = {};
    if (isMsdk) {
      data = {
        appid: getUrlParam('appid'),
        openid: getUrlParam('openid'),
        algorithm: getUrlParam('algorithm'),
        version: getUrlParam('version'),
        timestamp: +getUrlParam('timestamp'),
        sig: getUrlParam('sig'),
        encode: 2,
        msdkEncodeParam,
      };
      loginType = data.appid.match(/^[0-9]+$/) ? 'qq' : 'wx';
    } else if (env.isWeishi) {
      // 腾讯微视支持使用票据转换打通登录态
      loginType = +cookie.get('iAuthType') === 1 ? 'qq' : 'wx';
    } else if (env.isQQSports) {
      // 腾讯体育支持使用票据转换打通登录态
      loginType = cookie.get('main_login');
    } else if (env.isQQMusic) {
      // QQ音乐, 目前只支持QQ登录态换票据,微信由于缺少access_token
      const tmeLoginType = +cookie.get('tmeLoginType');

      if (tmeLoginType === 2) {
        loginType = 'qq';
      } else if (tmeLoginType === 1) {
        loginType = 'wx';
      }
    }

    if (!loginType) { // 未登录
      return callback();
    }
    if (env.isWeishi) {
      if (loginType === 'qq' && cookie.get('sSessionKey')) {
        cookie.set('accessToken', cookie.get('sSessionKey'), {
          domain: '.qq.com',
          path: '/',
        });
      }
      // 原因微视安卓端在登录态打通版本8.19.1前，也写了视频票据，但写的是空数据，导致cookie污染问题
      clearRootDomainCookies();
    } else if (env.isQQMusic && loginType === 'qq' && cookie.get('uin') && cookie.get('skey')) {
      // 由于QQ音乐现网仍存在 uin/skey 票据，兼容处理
      return skey2Vusession(callback);
    }

    // type: 2 // 1|非游戏QQ互联，2|非游戏微信，3|游戏msdk
    let type;
    if (isMsdk) {
      type = 3;
    } else if (loginType === 'qq') {
      type = 1;
    } else {
      type = 2;
    }

    request(`//vip.video.qq.com/fcgi-bin/comm_cgi?name=third_login&otype=xjson&type=${type}`, JSON.stringify(data), {
      type: 'post',
      dataType: 'json',
      xhrFields: { withCredentials: true },
      timeout: 2000,
    })
      .then((res) => {
        const options = {
          domain: getDomain(),
          expires: COOKIE_EXPIRED_TIME,
          path: '/',
        };
        cookie.set(`${loginType}_nick`, encodeURIComponent(res.nick), options);
        cookie.set(`${loginType}_head`, encodeURIComponent(res.head), options);
        callback();
      })
      .catch((e) => {
        logger.error('innerToken2tenvideo caught error', e);
        if (retryTime < 1) {
          innerToken2tenvideo(callback);
          retryTime += 1;
        } else {
          callback();
        }
      });
  });
}

/**
 * 清除登录态
 * @param {String} [type] 账号类型
 * @param {Function} callback 回调
 */
export function clearCookie(type, callback) {
  if ((cookie.get('uin') || cookie.get('luin'))) {
    config.PTLOGIN_COOKIE.forEach((key) => {
      cookie.remove(key, {
        domain: 'qq.com',
        path: '/',
      });
    });
  }

  const domain = getDomain();
  // 清除上次续期的时间
  cookie.remove('last_refresh_time', {
    domain,
    path: '/',
  });
  cookie.remove('last_refresh_vuserid', {
    domain,
    path: '/',
  });

  if (typeof type === 'function') {
    callback = type;
    type = loginType() || 'qq';
  }

  // 清除当前域下 Cookie 信息
  clearPageCookies();

  authLogout(type).then(callback);
}

/**
 *
 * @param {String} type 账号类型
 * @param {Function} callback 回调函数
 */
export function authLogout(type) {
  const params = { type };

  const wxAppid = cookie.get('appid');
  if (type === 'qq' && wxAppid && wxAppid.match(/^wx/)) {
    // 20210521: 强匹配微信appid，因为存在老qq登录时，appid=3000501，这种appid会导致后台取值异常
    // 同时存在QQ和微信登录时，由于原先V站切互联时遇到一些未明原因的问题，juliehwang 在清QQ登录态时，把微信的 appid 也清了
    // 现在增加参数来确保不清微信的 appid
    params.keepwx = 1;
  }

  if (!params.type) {
    // 默认清掉微信和QQ在当前页面的票据，避免票据污染，特别在升级登录态打通场景
    // 容易出现 film.qq.com 的cookie与 qq.com 的cookie相互影响
    params.type = loginType() || 'qq';
  }

  return vRequest('auth_logout', params, {
    timeout: 10000,
  })
    .catch((e) => {
      logger.error('auth_logout caught error', e);
      return null;
    });
}

// 获取QQ的本地 openapi 授权
export function requestQQNativeAuth() {
  return new Promise((resolve, reject) => {
    util.loadScript('https://g.gtimg.cn/libs/nativeapi/v1.0.0/Native.js').then(() => {
      // eslint-disable-next-line no-undef
      NativeApi.invoke('config', {
        appid: '101527197',
        permission(err) {
          reject(err);
        },
        success() {
          // eslint-disable-next-line no-undef
          NativeApi.invoke('loginSilent', {
            success(data) {
              resolve({
                access_token: data.access_token,
                openid: data.openid,
              });
            },
            failure(err) {
              reject(err);
            },
          });
        },
        failure(err) {
          reject(err);
        },
      });
    });
  });
}

// 同步手q互联登录态
export function requestQQLogin(params = {}) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => { // 超时抛异常
      reject(new Error('QQLogin timeout'));
      reporter.report({
        f_event: reporter.EVENT_LOGIN_TIMEOUT,
      });
    }, 5000);
    window.qqlogin_onSuccess = (json) => {
      try {
        const data = JSON.parse(json);
        const cookieDomain = getDomain();
        const cookieOption = {
          expires: COOKIE_EXPIRED_TIME,
          domain: cookieDomain,
          path: '/',
        };
        cookie.set('qq_nick', data.nick, cookieOption);
        cookie.set('qq_head', data.head, cookieOption);
        resolve(data);
        reporter.report({
          f_event: reporter.EVENT_LOGIN_AUTH,
        });
      } catch (e) {
        reject(new Error('login parse failed'));
      }
      clearTimeout(timer);
    };
    window.qqlogin_onInvokeFail = (json) => {
      try {
        const data = JSON.parse(json);
        reject(data);
      } catch (e) {
        reject(new Error('login parse failed'));
      }
      reporter.report({
        f_event: reporter.EVENT_LOGIN_FAIL,
      });
      clearTimeout(timer);
    };

    const authUrl = buildUrl(`//${isDebug ? 'debug' : ''}access.video.qq.com/user/auth_login`, {
      vappid: VAPPID,
      vsecret: VSECRET,
      type: 'qq',
      appid: '101527197',
      raw: 1,
      clear: 1,
      ...params,
    }, true);

    const ifa = document.createElement('iframe');
    ifa.src = authUrl;
    ifa.id = Math.random().toString(36)
      .substr(2);
    ifa.style.display = 'none';
    document.body.appendChild(ifa);
  });
}

/**
 * 根据登录态生成 gtk 加密串，用于校验视频票据
 * @return {Object}
 * @property {string} g_tk skey 票据.
 * @property {string} g_vstk 互联和微信 vusession 票据.
 * @property {string} g_actk 互联和微信 access_token 票据.
 */
export function buildCSRF() {
  const params = {};
  const type = loginType();

  if (type === 'qq') {
    const skey = getSkey();
    if (skey) {
      params.g_tk = time33(skey);
    }
    const vs = cookie.get('vqq_vusession');
    if (vs) {
      params.g_vstk = time33(vs);
    }
    const ac = cookie.get('vqq_access_token');
    if (ac) {
      params.g_actk = time33(ac);
    }
  } else if (type === 'wx') {
    const vs = cookie.get('vusession');
    if (vs) {
      params.g_vstk = time33(vs);
    }
    const ac = cookie.get('access_token');
    if (ac) {
      params.g_actk = time33(ac);
    }
  }
  return params;
}

/**
 * 获取QQ号
 * @returns {number}
 */
export function getUin() {
  if (getSkey() === '') {
    return 0;
  }
  let uin = parseInt(cookie.get('uin').replace(/^o0*/g, ''), 10);
  if (!uin || uin <= 10000) {
    uin = parseInt(cookie.get('luin').replace(/^o0*/g, ''), 10);
    if (!uin || uin <= 10000) {
      return 0;
    }
  }
  return uin;
}

/**
 * 获取 skey
 * @returns {string} skey
 */
export function getSkey() {
  const skey = cookie.get('skey') || cookie.get('lskey');
  return skey ? skey.trim() : '';
}

/**
 * 获取 PTLogin token
 * @returns {string}
 */
export function getToken() {
  const skey = getSkey();
  return skey ? time33(skey) : '';
}

/**
 * 哈希time33算法
 */
export function time33(skey) {
  let hash = 5381;
  for (let i = 0, len = skey.length; i < len; ++i) {
    hash += (hash << 5) + skey.charAt(i).charCodeAt();
  }
  return hash & 0x7fffffff;
}
